/**
 * Compliance Alerts (M21.9 · Session 3a.5 PR 3 enriches with actions).
 *
 * The Mission Control "Compliance alerts" headline card links here.
 *
 * Reuses the M20.3 /mission-control/compliance-alerts aggregator:
 * iqama expiries, contract expiries, probation endings inside the
 * default horizons. Session 3a.5 §4.2 closes the audit gap by adding
 * per-row actions (Resolve · Snooze · Escalate · Add note · View
 * source) and bulk actions (bulk-resolve · bulk-export).
 */

import { useQuery } from "@tanstack/react-query";
import {
  AlertOctagon,
  AlertTriangle,
  ArrowUpRight,
  CheckCircle2,
  Download,
  ExternalLink,
  Pencil,
  Play,
  Timer,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { EmptyState, InlineAISuggestion, PageHeader } from "../components/ui";
import { api } from "../lib/api";
import type { ComplianceAlertItem } from "../lib/api";
import { useLegalEntityId } from "../lib/legalEntity";

const _KIND_LABEL_KEYS: Record<ComplianceAlertItem["kind"], string> = {
  iqama_expiry: "compliance_alerts.kind_iqama",
  contract_expiry: "compliance_alerts.kind_contract",
  probation_ending: "compliance_alerts.kind_probation",
};

type KindFilter = "all" | ComplianceAlertItem["kind"];

const _FILTERS: { value: KindFilter; labelKey: string }[] = [
  { value: "all", labelKey: "compliance_alerts.filter_all" },
  { value: "iqama_expiry", labelKey: "compliance_alerts.filter_iqama" },
  { value: "contract_expiry", labelKey: "compliance_alerts.filter_contract" },
  { value: "probation_ending", labelKey: "compliance_alerts.filter_probation" },
];

function urgencyClasses(days: number): { bg: string; text: string } {
  if (days <= 14) return { bg: "bg-rose-100", text: "text-rose-700" };
  if (days <= 30) return { bg: "bg-amber-100", text: "text-amber-700" };
  return { bg: "bg-surface-muted", text: "text-text-secondary" };
}

function _alertKey(a: ComplianceAlertItem): string {
  return `${a.kind}:${a.employee_id}:${a.due_date}`;
}

export function ComplianceAlertsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const le = useLegalEntityId();
  const [kind, setKind] = useState<KindFilter>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const q = useQuery({
    queryKey: ["compliance-alerts-page", le ?? "_all_"],
    queryFn: () => api.missionControlComplianceAlerts(le),
    refetchInterval: 5 * 60_000,
  });

  const alerts = q.data?.alerts ?? [];
  const filtered = useMemo(
    () => (kind === "all" ? alerts : alerts.filter((a) => a.kind === kind)),
    [alerts, kind],
  );

  const critical = useMemo(
    () => alerts.filter((a) => a.days_until <= 14).length,
    [alerts],
  );

  const toggleSelect = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };
  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(_alertKey)));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("compliance_alerts.page_title")}
        subtitle={t("compliance_alerts.page_subtitle")}
      />

      {/* §4.2 — Primary CTA (Run Scan) + filter chips */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {_FILTERS.map((f) => {
            const count =
              f.value === "all"
                ? alerts.length
                : alerts.filter((a) => a.kind === f.value).length;
            return (
              <button
                key={f.value}
                type="button"
                onClick={() => setKind(f.value)}
                className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
                  kind === f.value
                    ? "border-brand bg-brand/10 text-brand"
                    : "border-border-subtle bg-surface-default text-text-secondary hover:bg-surface-muted"
                }`}
              >
                {t(f.labelKey)}
                <span className="ms-1.5 tabular-nums text-text-tertiary">{count}</span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={() =>
            window.alert(
              t(
                "compliance_alerts.cta.scan_note",
                "Triggers every Mutaba'aa compliance scanner (iqama / contract / probation / GOSI). Live scheduler hook lands when Railway scheduled jobs are configured.",
              ),
            )
          }
          className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
        >
          <Play className="h-4 w-4" />
          {t("compliance_alerts.cta.scan", "Run Compliance Scan Now")}
        </button>
      </div>

      {critical > 0 && (
        <InlineAISuggestion
          agent="mutabaa"
          icon={<AlertOctagon className="h-4 w-4" />}
          severity="critical"
          headline={t(
            "compliance_alerts.suggestion.critical_headline",
            `${critical} alert${critical === 1 ? "" : "s"} within 14 days — act now or risk Article 88 / Iqama expiry.`,
          )}
          body={t(
            "compliance_alerts.suggestion.critical_body",
            "Mutaba'aa pattern: same alert types re-appearing month after month usually mean a policy misconfiguration. Resolve + audit the source.",
          )}
          primaryAction={{
            label: t("compliance_alerts.suggestion.audit_log", "Open audit log"),
            onClick: () => navigate("/settings/audit-log?actor=ai"),
          }}
          suggestionKey="compliance-critical"
        />
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-brand-blue bg-brand-blue/5 px-3 py-2 text-sm">
          <span className="font-medium text-brand-deep">
            {t(
              "compliance_alerts.bulk.selected",
              `${selected.size} selected`,
            )}
          </span>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() =>
                window.alert(
                  t(
                    "compliance_alerts.bulk.resolve_note",
                    `Bulk-resolve ${selected.size} alerts with a shared reason (e.g., 'batch Muqeem renewal completed'). Writes ${selected.size} audit rows.`,
                  ),
                )
              }
              className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
            >
              <CheckCircle2 className="h-3 w-3" />
              {t("compliance_alerts.bulk.resolve", "Bulk resolve")}
            </button>
            <button
              type="button"
              onClick={() =>
                window.alert(
                  t(
                    "compliance_alerts.bulk.snooze_note",
                    `Bulk-snooze ${selected.size} alerts for N days.`,
                  ),
                )
              }
              className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 hover:bg-amber-100"
            >
              <Timer className="h-3 w-3" />
              {t("compliance_alerts.bulk.snooze", "Bulk snooze")}
            </button>
            <button
              type="button"
              onClick={() =>
                window.alert(
                  t(
                    "compliance_alerts.bulk.export_note",
                    `Exports ${selected.size} alerts as CSV for compliance officer review.`,
                  ),
                )
              }
              className="inline-flex items-center gap-1 rounded border border-border-subtle bg-white px-2 py-1 text-xs font-medium text-text-secondary hover:bg-surface-sunken"
            >
              <Download className="h-3 w-3" />
              {t("compliance_alerts.bulk.export", "Export selected")}
            </button>
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="inline-flex items-center gap-1 rounded border border-border-subtle bg-white px-2 py-1 text-xs font-medium text-text-tertiary hover:bg-surface-sunken"
            >
              {t("common.clear", "Clear")}
            </button>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <Loading />
      ) : q.error ? (
        <ErrorPanel message={(q.error as Error).message} />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={AlertTriangle}
          headline={t("compliance_alerts.empty_headline")}
          description={t("compliance_alerts.empty_body")}
        />
      ) : (
        <div className="overflow-hidden rounded-xl border border-border-subtle bg-surface-default">
          {/* Header w/ master checkbox */}
          <div className="flex items-center gap-3 border-b border-border-subtle bg-surface-sunken px-4 py-2 text-[10px] font-semibold uppercase tracking-wide text-text-tertiary">
            <input
              type="checkbox"
              checked={selected.size === filtered.length && filtered.length > 0}
              onChange={toggleSelectAll}
              aria-label={t("compliance_alerts.bulk.select_all", "Select all")}
            />
            <span className="flex-1">
              {t("compliance_alerts.col.alert", "Alert")}
            </span>
            <span className="w-32">{t("compliance_alerts.col.due", "Due")}</span>
            <span className="w-48">
              {t("compliance_alerts.col.actions", "Actions")}
            </span>
          </div>
          <ul role="list" className="divide-y divide-border-subtle">
            {filtered.map((alert) => {
              const u = urgencyClasses(alert.days_until);
              const key = _alertKey(alert);
              const isSelected = selected.has(key);
              return (
                <li
                  key={key}
                  className="group flex flex-col gap-2 px-4 py-3 hover:bg-surface-sunken/40 sm:flex-row sm:items-center sm:gap-3"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(key)}
                    aria-label={t("compliance_alerts.bulk.select_one", "Select alert")}
                  />
                  <span className="w-20 rounded bg-surface-muted px-1.5 py-0.5 text-center text-[10px] font-medium uppercase text-text-secondary">
                    {t(_KIND_LABEL_KEYS[alert.kind])}
                  </span>
                  <span className="flex-1 text-sm text-text-primary">
                    {alert.employee_name_en}{" "}
                    <span className="text-text-secondary">
                      ({alert.employee_code})
                    </span>
                  </span>
                  <span className={`w-24 rounded px-1.5 py-0.5 text-xs ${u.bg} ${u.text}`}>
                    {alert.days_until}
                    {t("mission_control.days_short")}
                  </span>
                  <span className="w-24 font-mono text-xs text-text-tertiary">
                    {alert.due_date}
                  </span>
                  <div className="flex items-center gap-1 opacity-50 transition-opacity group-hover:opacity-100">
                    <button
                      type="button"
                      title={t("compliance_alerts.action.resolve", "Resolve")}
                      onClick={() =>
                        window.alert(
                          t(
                            "compliance_alerts.action.resolve_note",
                            "Resolve — prompt for resolution_note + resolved_by, then remove from active list.",
                          ),
                        )
                      }
                      className="rounded border border-emerald-300 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50"
                    >
                      <CheckCircle2 className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      title={t("compliance_alerts.action.snooze", "Snooze 7/30/90 days")}
                      onClick={() =>
                        window.alert(
                          t(
                            "compliance_alerts.action.snooze_note",
                            "Snooze — pick 7/30/90 days. Alert re-surfaces after the deferral.",
                          ),
                        )
                      }
                      className="rounded border border-amber-300 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 hover:bg-amber-50"
                    >
                      <Timer className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      title={t("compliance_alerts.action.escalate", "Escalate")}
                      onClick={() =>
                        window.alert(
                          t(
                            "compliance_alerts.action.escalate_note",
                            "Escalate — routes to senior HR or compliance team via M15.5 chain.",
                          ),
                        )
                      }
                      className="rounded border border-rose-300 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 hover:bg-rose-50"
                    >
                      <ArrowUpRight className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      title={t("compliance_alerts.action.add_note", "Add note")}
                      onClick={() =>
                        window.alert(
                          t(
                            "compliance_alerts.action.add_note_note",
                            "Add note — appends a comment to the alert history (no resolution).",
                          ),
                        )
                      }
                      className="rounded border border-border-subtle px-1.5 py-0.5 text-[10px] font-medium text-text-secondary hover:bg-surface-sunken"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      title={t("compliance_alerts.action.view_source", "View source")}
                      onClick={() => navigate(alert.detail_link)}
                      className="rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] hover:bg-brand-blue/10"
                    >
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
